import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type HyperTextProps = {
  text: string;
  duration?: number;
  className?: string;
  animateOnLoad?: boolean;
};

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function getRandomLetter() {
  return alphabet[Math.floor(Math.random() * alphabet.length)] ?? "A";
}

function shouldReduceMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function HyperText({ text, duration = 760, className, animateOnLoad = true }: HyperTextProps) {
  const normalizedText = useMemo(() => text.toUpperCase(), [text]);
  const letters = useMemo(() => normalizedText.split(""), [normalizedText]);
  const [displayText, setDisplayText] = useState(letters);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<number | undefined>(undefined);

  const triggerAnimation = useCallback(() => {
    if (shouldReduceMotion()) {
      setDisplayText(letters);
      return;
    }

    window.clearInterval(intervalRef.current);
    setIsAnimating(true);

    const startedAt = Date.now();
    intervalRef.current = window.setInterval(() => {
      const progress = Math.min(letters.length, ((Date.now() - startedAt) / duration) * letters.length);

      setDisplayText(
        letters.map((letter, index) => {
          if (letter === " ") {
            return letter;
          }

          return index <= progress ? letter : getRandomLetter();
        })
      );

      if (progress >= letters.length) {
        window.clearInterval(intervalRef.current);
        setDisplayText(letters);
        setIsAnimating(false);
      }
    }, 32);
  }, [duration, letters]);

  useEffect(() => {
    setDisplayText(letters);

    if (animateOnLoad) {
      triggerAnimation();
    }

    return () => window.clearInterval(intervalRef.current);
  }, [animateOnLoad, letters, triggerAnimation]);

  return (
    <span
      className={["hyper-text", isAnimating ? "is-animating" : "", className].filter(Boolean).join(" ")}
      aria-label={normalizedText}
      onMouseEnter={triggerAnimation}
    >
      {displayText.map((letter, index) => (
        <span
          key={index}
          aria-hidden="true"
          className={letter === " " ? "hyper-text-letter hyper-text-space" : "hyper-text-letter"}
        >
          {letter}
        </span>
      ))}
    </span>
  );
}
